import { isAllowedModernFramePathname } from './modern-frame-namespaces.js';
import { toCanonicalModernUrl } from './modern-frame-url.js';
import {
  MAX_MODERN_FRAME_ERROR_MESSAGE_LENGTH,
  MAX_MODERN_FRAME_IDENTIFIER_LENGTH,
  MAX_MODERN_FRAME_TITLE_LENGTH,
  MODERN_FRAME_PROTOCOL,
  MODERN_FRAME_VERSION,
  type HostToModernFrameMessageV2,
  type ModernFrameChannelMessageV2,
  type ModernFrameHelloMessageV2,
  type ModernFrameHostConnectMessageV2,
  type ModernFrameToHostMessageV2,
  type ModernFrameWindowMessageV2,
} from './modern-frame.v2.js';

type UnknownRecord = Record<string, unknown>;

export interface ModernFrameEventValidationOptions {
  readonly expectedOrigin: string;
  readonly expectedSource: MessageEventSource;
}

export interface ModernFrameChannelValidationOptions {
  readonly expectedFrameInstanceId: string;
  readonly expectedHostSessionId: string;
}

export interface ParsedModernFrameHostConnectV2 {
  readonly message: ModernFrameHostConnectMessageV2;
  readonly port: MessagePort;
}

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const hasOnlyKeys = (value: UnknownRecord, allowedKeys: readonly string[]): boolean =>
  Object.keys(value).every((key) => allowedKeys.includes(key));
const isBoundedString = (value: unknown, maximum: number): value is string =>
  typeof value === 'string' && value.trim().length > 0 && value.length <= maximum;
const isIdentifier = (value: unknown): value is string =>
  isBoundedString(value, MAX_MODERN_FRAME_IDENTIFIER_LENGTH);
const isOptionalReason = (value: unknown): value is string | undefined =>
  value === undefined || isBoundedString(value, MAX_MODERN_FRAME_IDENTIFIER_LENGTH);
const hasBaseEnvelope = (value: UnknownRecord): boolean =>
  value['protocol'] === MODERN_FRAME_PROTOCOL && value['version'] === MODERN_FRAME_VERSION;

const isUserContext = (value: unknown): boolean =>
  isRecord(value) &&
  hasOnlyKeys(value, ['userId', 'displayName', 'email', 'profiles']) &&
  typeof value['userId'] === 'number' &&
  Number.isSafeInteger(value['userId']) &&
  value['userId'] > 0 &&
  isBoundedString(value['displayName'], MAX_MODERN_FRAME_TITLE_LENGTH) &&
  isBoundedString(value['email'], MAX_MODERN_FRAME_TITLE_LENGTH) &&
  Array.isArray(value['profiles']) &&
  value['profiles'].length <= 64 &&
  value['profiles'].every((profile) => isBoundedString(profile, MAX_MODERN_FRAME_TITLE_LENGTH));

export function isAllowedModernFrameRoute(value: unknown): value is string {
  if (typeof value !== 'string' || value.trim().length === 0) return false;
  const canonicalUrl = toCanonicalModernUrl(value);
  if (canonicalUrl === null) return false;
  const pathname = canonicalUrl.split(/[?#]/, 1)[0] ?? '';
  return isAllowedModernFramePathname(pathname);
}

/** Returns true when the value is a safe relative shell route that is not a modern frame route. */
export function isSafeShellRoute(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const canonical = toCanonicalModernUrl(value);
  return canonical !== null && !isAllowedModernFrameRoute(canonical);
}

const hasViewportInsets = (value: unknown): boolean =>
  isRecord(value) &&
  hasOnlyKeys(value, ['top']) &&
  typeof value['top'] === 'number' &&
  Number.isFinite(value['top']) &&
  value['top'] >= 0;
const hasReasonPayload = (value: unknown): boolean =>
  isRecord(value) && hasOnlyKeys(value, ['reason']) && isOptionalReason(value['reason']);
const hasRoutePayload = (value: unknown, key: 'path' | 'canonicalPath'): boolean =>
  isRecord(value) && hasOnlyKeys(value, [key]) && isAllowedModernFrameRoute(value[key]);
const isNavigationTrigger = (value: unknown): boolean =>
  value === 'imperative' || value === 'popstate' || value === 'hashchange';
const hasActivatePayload = (value: unknown): boolean =>
  isRecord(value) &&
  hasOnlyKeys(value, ['canonicalPath', 'viewportInsets', 'trigger', 'replace']) &&
  isAllowedModernFrameRoute(value['canonicalPath']) &&
  (value['viewportInsets'] === undefined || hasViewportInsets(value['viewportInsets'])) &&
  (value['trigger'] === undefined || isNavigationTrigger(value['trigger'])) &&
  (value['replace'] === undefined || typeof value['replace'] === 'boolean');
const hasNavigatePayload = (value: unknown): boolean =>
  isRecord(value) &&
  hasOnlyKeys(value, ['path', 'replace', 'trigger']) &&
  isAllowedModernFrameRoute(value['path']) &&
  (value['replace'] === undefined || typeof value['replace'] === 'boolean') &&
  (value['trigger'] === undefined || isNavigationTrigger(value['trigger']));
const hasShellNavigatePayload = (value: unknown): boolean =>
  isRecord(value) &&
  hasOnlyKeys(value, ['path', 'replace']) &&
  isSafeShellRoute(value['path']) &&
  (value['replace'] === undefined || typeof value['replace'] === 'boolean');
const hasOverlayPayload = (value: unknown): boolean =>
  isRecord(value) &&
  hasOnlyKeys(value, ['overlayId', 'open']) &&
  isIdentifier(value['overlayId']) &&
  typeof value['open'] === 'boolean';
const hasOverlayReadyPayload = (value: unknown): boolean =>
  isRecord(value) &&
  hasOnlyKeys(value, ['overlayId']) &&
  isIdentifier(value['overlayId']);

export function isModernFrameHelloMessageV2(value: unknown): value is ModernFrameHelloMessageV2 {
  return isRecord(value) &&
    hasBaseEnvelope(value) &&
    hasOnlyKeys(value, ['protocol', 'version', 'type', 'frameInstanceId', 'supportedVersions']) &&
    value['type'] === 'FRAME_HELLO' &&
    isIdentifier(value['frameInstanceId']) &&
    Array.isArray(value['supportedVersions']) &&
    value['supportedVersions'].length > 0 &&
    value['supportedVersions'].length <= 4 &&
    value['supportedVersions'].every((version) => Number.isSafeInteger(version) && version > 0) &&
    new Set(value['supportedVersions']).size === value['supportedVersions'].length &&
    value['supportedVersions'].includes(MODERN_FRAME_VERSION);
}

export function isModernFrameHostConnectMessageV2(value: unknown): value is ModernFrameHostConnectMessageV2 {
  return isRecord(value) &&
    hasBaseEnvelope(value) &&
    hasOnlyKeys(value, ['protocol', 'version', 'type', 'frameInstanceId', 'hostSessionId']) &&
    value['type'] === 'HOST_CONNECT' &&
    isIdentifier(value['frameInstanceId']) &&
    isIdentifier(value['hostSessionId']);
}

export function isModernFrameWindowMessageV2(value: unknown): value is ModernFrameWindowMessageV2 {
  return isModernFrameHelloMessageV2(value) || isModernFrameHostConnectMessageV2(value);
}

export function isModernFrameChannelMessageV2(value: unknown): value is ModernFrameChannelMessageV2 {
  if (!isRecord(value) || !hasBaseEnvelope(value) ||
    !isIdentifier(value['frameInstanceId']) || !isIdentifier(value['hostSessionId'])) {
    return false;
  }

  switch (value['type']) {
    case 'CHANNEL_READY':
      return hasOnlyKeys(value, ['protocol', 'version', 'type', 'frameInstanceId', 'hostSessionId']);
    case 'SESSION_CONTEXT':
      return hasOnlyKeys(value, ['protocol', 'version', 'type', 'frameInstanceId', 'hostSessionId', 'payload']) &&
        isRecord(value['payload']) && hasOnlyKeys(value['payload'], ['user']) &&
        isUserContext(value['payload']['user']);
    case 'LOGOUT':
    case 'AUTH_REQUIRED':
      return hasOnlyKeys(value, ['protocol', 'version', 'type', 'frameInstanceId', 'hostSessionId', 'payload']) &&
        hasReasonPayload(value['payload']);
    case 'ACTIVATE_ROUTE':
      return hasOnlyKeys(value, [
        'protocol', 'version', 'type', 'frameInstanceId', 'hostSessionId', 'navigationId', 'payload',
      ]) && isIdentifier(value['navigationId']) && hasActivatePayload(value['payload']);
    case 'ROUTE_COMMITTED':
      return hasOnlyKeys(value, [
        'protocol', 'version', 'type', 'frameInstanceId', 'hostSessionId', 'navigationId', 'payload',
      ]) && isIdentifier(value['navigationId']) && hasRoutePayload(value['payload'], 'path');
    case 'ROUTE_FAILED':
      return hasOnlyKeys(value, [
        'protocol', 'version', 'type', 'frameInstanceId', 'hostSessionId', 'navigationId', 'payload',
      ]) && isIdentifier(value['navigationId']) && isRecord(value['payload']) &&
        hasOnlyKeys(value['payload'], ['path', 'code']) &&
        isAllowedModernFrameRoute(value['payload']['path']) && isIdentifier(value['payload']['code']);
    case 'DEACTIVATE_ROUTE':
    case 'ROUTE_DEACTIVATED':
      return hasOnlyKeys(value, [
        'protocol', 'version', 'type', 'frameInstanceId', 'hostSessionId', 'navigationId',
      ]) && isIdentifier(value['navigationId']);
    case 'AUTHORIZE_ROUTE_REQUEST':
      return hasOnlyKeys(value, [
        'protocol', 'version', 'type', 'frameInstanceId', 'hostSessionId', 'requestId', 'payload',
      ]) && isIdentifier(value['requestId']) && isRecord(value['payload']) &&
        hasOnlyKeys(value['payload'], ['canonicalRoute']) &&
        isAllowedModernFrameRoute(value['payload']['canonicalRoute']);
    case 'AUTHORIZE_ROUTE_RESULT':
      return hasOnlyKeys(value, [
        'protocol', 'version', 'type', 'frameInstanceId', 'hostSessionId', 'requestId', 'payload',
      ]) && isIdentifier(value['requestId']) && isRecord(value['payload']) &&
        hasOnlyKeys(value['payload'], ['result']) &&
        ['allowed', 'denied', 'auth-required', 'unavailable'].includes(String(value['payload']['result']));
    case 'TOKEN_REFRESH_REQUEST':
      return hasOnlyKeys(value, [
        'protocol', 'version', 'type', 'frameInstanceId', 'hostSessionId', 'requestId',
      ]) && isIdentifier(value['requestId']);
    case 'TOKEN_REFRESH_RESULT':
      return hasOnlyKeys(value, [
        'protocol', 'version', 'type', 'frameInstanceId', 'hostSessionId', 'requestId', 'payload',
      ]) && isIdentifier(value['requestId']) && isRecord(value['payload']) &&
        hasOnlyKeys(value['payload'], ['success']) && typeof value['payload']['success'] === 'boolean';
    case 'NAVIGATE_REQUEST':
      return hasOnlyKeys(value, [
        'protocol', 'version', 'type', 'frameInstanceId', 'hostSessionId', 'navigationId', 'payload',
      ]) && isIdentifier(value['navigationId']) && hasNavigatePayload(value['payload']);
    case 'SHELL_NAVIGATE_REQUEST':
      return hasOnlyKeys(value, [
        'protocol', 'version', 'type', 'frameInstanceId', 'hostSessionId', 'navigationId', 'payload',
      ]) && isIdentifier(value['navigationId']) && hasShellNavigatePayload(value['payload']);
    case 'TITLE_CHANGED':
      return hasOnlyKeys(value, [
        'protocol', 'version', 'type', 'frameInstanceId', 'hostSessionId', 'navigationId', 'payload',
      ]) && isIdentifier(value['navigationId']) && isRecord(value['payload']) &&
        hasOnlyKeys(value['payload'], ['title']) &&
        isBoundedString(value['payload']['title'], MAX_MODERN_FRAME_TITLE_LENGTH);
    case 'OVERLAY_CHANGED':
      return hasOnlyKeys(value, [
        'protocol', 'version', 'type', 'frameInstanceId', 'hostSessionId', 'payload',
      ]) && hasOverlayPayload(value['payload']);
    case 'OVERLAY_READY':
      return hasOnlyKeys(value, [
        'protocol', 'version', 'type', 'frameInstanceId', 'hostSessionId', 'payload',
      ]) && hasOverlayReadyPayload(value['payload']);
    case 'USER_ACTIVITY':
      return hasOnlyKeys(value, ['protocol', 'version', 'type', 'frameInstanceId', 'hostSessionId', 'payload']) &&
        isRecord(value['payload']) && hasOnlyKeys(value['payload'], ['timestamp']) &&
        typeof value['payload']['timestamp'] === 'number' &&
        Number.isSafeInteger(value['payload']['timestamp']) && value['payload']['timestamp'] > 0;
    case 'FRAME_ERROR':
      return hasOnlyKeys(value, [
        'protocol', 'version', 'type', 'frameInstanceId', 'hostSessionId',
        'requestId', 'navigationId', 'payload',
      ]) && (value['requestId'] === undefined || isIdentifier(value['requestId'])) &&
        (value['navigationId'] === undefined || isIdentifier(value['navigationId'])) &&
        isRecord(value['payload']) &&
        hasOnlyKeys(value['payload'], ['code', 'scope', 'message', 'recoverable']) &&
        isIdentifier(value['payload']['code']) &&
        ['document', 'handshake', 'navigation', 'authorization', 'frame'].includes(String(value['payload']['scope'])) &&
        isBoundedString(value['payload']['message'], MAX_MODERN_FRAME_ERROR_MESSAGE_LENGTH) &&
        typeof value['payload']['recoverable'] === 'boolean';
    default:
      return false;
  }
}

export function isHostToModernFrameMessageV2(value: unknown): value is HostToModernFrameMessageV2 {
  return isModernFrameChannelMessageV2(value) && [
    'SESSION_CONTEXT',
    'LOGOUT',
    'ACTIVATE_ROUTE',
    'DEACTIVATE_ROUTE',
    'AUTHORIZE_ROUTE_RESULT',
    'TOKEN_REFRESH_RESULT',
    'OVERLAY_READY',
  ].includes(value.type);
}

export function isModernFrameToHostMessageV2(value: unknown): value is ModernFrameToHostMessageV2 {
  return isModernFrameChannelMessageV2(value) && [
    'CHANNEL_READY',
    'ROUTE_COMMITTED',
    'ROUTE_FAILED',
    'ROUTE_DEACTIVATED',
    'AUTHORIZE_ROUTE_REQUEST',
    'TOKEN_REFRESH_REQUEST',
    'NAVIGATE_REQUEST',
    'SHELL_NAVIGATE_REQUEST',
    'TITLE_CHANGED',
    'OVERLAY_CHANGED',
    'AUTH_REQUIRED',
    'USER_ACTIVITY',
    'FRAME_ERROR',
  ].includes(value.type);
}

export function isAllowedModernFrameOrigin(actualOrigin: string, expectedOrigin: string): boolean {
  try {
    return new URL(actualOrigin).origin === new URL(expectedOrigin).origin;
  } catch {
    return false;
  }
}

export function parseModernFrameHelloEventV2(
  event: Pick<MessageEvent<unknown>, 'data' | 'origin' | 'source'>,
  options: ModernFrameEventValidationOptions,
): ModernFrameHelloMessageV2 | null {
  if (!isAllowedModernFrameOrigin(event.origin, options.expectedOrigin) ||
    event.source !== options.expectedSource || !isModernFrameHelloMessageV2(event.data)) {
    return null;
  }
  return event.data;
}

export function parseModernFrameHostConnectEventV2(
  event: Pick<MessageEvent<unknown>, 'data' | 'origin' | 'source' | 'ports'>,
  options: ModernFrameEventValidationOptions,
): ParsedModernFrameHostConnectV2 | null {
  if (!isAllowedModernFrameOrigin(event.origin, options.expectedOrigin) ||
    event.source !== options.expectedSource || !isModernFrameHostConnectMessageV2(event.data) ||
    event.ports.length !== 1 || !event.ports[0]) {
    return null;
  }
  return { message: event.data, port: event.ports[0] };
}

function hasExpectedChannel(
  message: ModernFrameChannelMessageV2,
  options: ModernFrameChannelValidationOptions,
): boolean {
  return message.frameInstanceId === options.expectedFrameInstanceId &&
    message.hostSessionId === options.expectedHostSessionId;
}

export function parseHostToModernFrameMessageV2(
  value: unknown,
  options: ModernFrameChannelValidationOptions,
): HostToModernFrameMessageV2 | null {
  if (!isHostToModernFrameMessageV2(value) || !hasExpectedChannel(value, options)) return null;
  return value;
}

export function parseModernFrameToHostMessageV2(
  value: unknown,
  options: ModernFrameChannelValidationOptions,
): ModernFrameToHostMessageV2 | null {
  if (!isModernFrameToHostMessageV2(value) || !hasExpectedChannel(value, options)) return null;
  return value;
}
